import { Target, Sparkles, HelpCircle, Info } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useLeadTriage } from '../hooks/useCopilots';
import { AISuggestionBadge, AI_SUGGESTION_RING } from './AISuggestionBadge';
import {
  BAND_ORDER,
  BAND_LABELS,
  BAND_TONE,
  BAND_RANGE_LABEL,
  formatCount,
  DEPARTMENT_PERSONA,
} from '../constants';
import type { RankedLead, UnscoredLead } from '../types';
import { useUser } from '@/shared/hooks/useAuth';

/**
 * Lead Triage panel (Requirement 9). Renders the priority bands (high / medium /
 * low) with each lead's underlying numeric `scoreLead` value, plus the
 * `unscored` group shown separately with NO fabricated score. The whole surface
 * is an `AI_Suggestion` — marked with a ring + badge and traceable to the real
 * score behind every ranking (Req 9.4, 14.3, 14.5). An explicit empty state is
 * driven by the server's `empty` flag (Req 14.2).
 */
export function LeadTriagePanel({ enabled = true }: { enabled?: boolean }) {
  const { data, isLoading, isError } = useLeadTriage({ enabled });
  const user = useUser();
  const dept = user?.department || 'sales';
  const persona = DEPARTMENT_PERSONA[dept as keyof typeof DEPARTMENT_PERSONA] || DEPARTMENT_PERSONA['sales'];

  const rankedColumns: Column<RankedLead>[] = [
    {
      key: 'name',
      header: persona.triageEntity,
      render: (r) => <span className="font-medium text-on-surface">{r.name || `Unnamed ${persona.triageEntity.toLowerCase()}`}</span>,
    },
    {
      key: 'score',
      header: 'Underlying score',
      align: 'right',
      render: (r) => (
        // The real scoreLead value — shown so the ranking is traceable (Req 9.4, 14.5).
        <span className="inline-flex items-center gap-2">
          <span className="tabular-nums font-semibold text-on-surface">{r.score}</span>
          <span className="text-body-sm text-on-surface-variant">/ 100</span>
        </span>
      ),
    },
  ];

  const unscoredColumns: Column<UnscoredLead>[] = [
    {
      key: 'name',
      header: persona.triageEntity,
      render: (r) => <span className="font-medium text-on-surface">{r.name || `Unnamed ${persona.triageEntity.toLowerCase()}`}</span>,
    },
    {
      key: 'score',
      header: 'Underlying score',
      align: 'right',
      // No fabricated score — the server intentionally omits one (Req 9.5).
      render: () => <span className="text-body-sm text-on-surface-variant">Not scored</span>,
    },
  ];

  const counts = data?.counts;

  return (
    <Card className={AI_SUGGESTION_RING}>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Target className="h-4 w-4" /> {persona.triageEntity} Triage
            <AISuggestionBadge />
          </span>
        }
        subtitle={`${persona.triageEntity}s ranked by the scoring model, grouped into priority bands. Every ranking shows its underlying score.`}
      />
      <CardBody className="space-y-6">
        {/* AI provenance note — keeps the suggestion traceable to real data. */}
        <div className="flex items-start gap-2 rounded-card border border-info/40 bg-info/5 p-3 text-body-sm text-on-surface-variant">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-info-text" />
          <p>
            These priority bands are an <span className="font-medium text-on-surface">AI Suggestion</span>,
            not a persisted CRM field. Each band is derived from the underlying score shown beside
            every {persona.triageEntity.toLowerCase()}; {persona.triageEntity.toLowerCase()}s with no scoring signals are listed separately with no score.
          </p>
        </div>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          <EmptyState
            title="Couldn't load triage"
            description="Check your connection or refresh to try again."
          />
        ) : data?.empty ? (
          <EmptyState
            icon={<Target className="h-6 w-6" />}
            title={`No ${persona.triageEntity.toLowerCase()}s to triage`}
            description={`There are no ${persona.triageEntity.toLowerCase()}s in your scope to rank yet, so there is nothing to suggest.`}
          />
        ) : (
          <>
            {/* Band counts overview */}
            {counts && (
              <div className="flex flex-wrap gap-2">
                {BAND_ORDER.map((band) => (
                  <Badge key={band} tone={BAND_TONE[band]}>
                    {BAND_LABELS[band]}: {formatCount(counts[band])}
                  </Badge>
                ))}
                <Badge tone="neutral">Unscored: {formatCount(counts.unscored)}</Badge>
              </div>
            )}

            {/* Priority bands, highest first */}
            {BAND_ORDER.map((band) => {
              const leads = data?.bands?.[band] ?? [];
              return (
                <section key={band} className="space-y-2">
                  <h4 className="flex items-center gap-2 text-data-md font-semibold text-on-surface">
                    <Badge tone={BAND_TONE[band]}>{BAND_LABELS[band]}</Badge>
                    <span className="text-body-sm font-normal text-on-surface-variant">
                      {BAND_RANGE_LABEL[band]} · {formatCount(leads.length)}
                    </span>
                  </h4>
                  <Table
                    columns={rankedColumns}
                    data={leads}
                    rowKey={(r) => r.id}
                    empty={`No ${BAND_LABELS[band].toLowerCase()} leads.`}
                  />
                </section>
              );
            })}

            {/* Unscored group — shown separately, with NO fabricated score (Req 9.5) */}
            <section className="space-y-2">
              <h4 className="flex items-center gap-2 text-data-md font-semibold text-on-surface">
                <HelpCircle className="h-4 w-4 text-on-surface-variant" /> Unscored
                <span className="text-body-sm font-normal text-on-surface-variant">
                  {formatCount(data?.unscored?.length ?? 0)}
                </span>
              </h4>
              <p className="flex items-start gap-2 text-body-sm text-on-surface-variant">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                These leads provide none of the signals the scoring model reads, so no score is shown —
                they are never assigned a fabricated value.
              </p>
              <Table
                columns={unscoredColumns}
                data={data?.unscored ?? []}
                rowKey={(r) => r.id}
                empty="No unscored leads."
              />
            </section>
          </>
        )}
      </CardBody>
    </Card>
  );
}
